import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, ArrowRight, Clock, User, Building, Rocket,
  Package, RefreshCw, ChevronRight,
} from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";

function Kpi({ label, value, icon: Icon, accent, muted, sub }) {
  return (
    <Card
      data-testid={`sub-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : muted ? "bg-slate-50" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

function StageDots({ stage, stages }) {
  return (
    <div className="flex items-center gap-1">
      {stages.map((s) => {
        const done = s.id < stage;
        const current = s.id === stage;
        return (
          <div key={s.id} className="flex items-center gap-1">
            <div
              className={`w-2.5 h-2.5 rounded-full ${
                done ? "bg-[#0F9B7F]" : current ? "bg-[#0033A0] ring-4 ring-[#0033A0]/20" : "bg-slate-300"
              }`}
              title={s.label}
            />
            {s.id < stages.length && <ChevronRight size={10} className="text-slate-300" />}
          </div>
        );
      })}
    </div>
  );
}

function relTime(ts) {
  if (!ts) return "";
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

function statusBadge(status) {
  if (status === "Approved") return "bg-[#0F9B7F]/10 text-[#0F9B7F] border-[#0F9B7F]/30";
  if (status === "Rejected") return "bg-[#DA291C]/10 text-[#DA291C] border-[#DA291C]/30";
  return "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30";
}

export default function AdminSubmissions() {
  const { userName, userEmail } = usePersona();
  const { brand } = useBranding();
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({});
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openSub, setOpenSub] = useState(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionAction, setDecisionAction] = useState("advance");
  const [decisionNote, setDecisionNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await endpoints.marketplaceSubmissions();
      setItems(res.items || []);
      setKpis(res.kpis || {});
      setStages(res.stages || []);
    } catch { toast.error("Could not load submissions"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = statusFilter === "all"
    ? items
    : statusFilter === "pending"
      ? items.filter((i) => i.status !== "Approved" && i.status !== "Rejected")
      : items.filter((i) => i.status === statusFilter);

  const openDecision = (item, action) => {
    setOpenSub(item);
    setDecisionAction(action);
    setDecisionNote("");
    setDecisionOpen(true);
  };

  const submitDecision = async () => {
    if (!openSub) return;
    setSaving(true);
    try {
      const doc = await endpoints.decideSubmission(openSub.id, {
        action: decisionAction,
        actor: userEmail || userName || "IT Admin",
        note: decisionNote || undefined,
      });
      toast.success(`Submission ${doc.status.toLowerCase()}`);
      setDecisionOpen(false);
      setOpenSub(null);
      load();
    } catch { toast.error("Decision failed"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-96" />
          <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}</div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-submissions-page">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0033A0] grid place-items-center">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small} · Flexspace · Admin</div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">
                AI Marketplace · Submission Approval Queue
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Admin view · IT Security + Governance</Badge>
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="submissions-refresh-btn" className="border-slate-300">
              <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Pending" value={kpis.pending || 0} icon={Clock} accent sub="Awaiting decision" />
          <Kpi label="Security review" value={kpis.in_security_review || 0} icon={ShieldCheck} sub="AV + SBOM scans" />
          <Kpi label="Awaiting admin" value={kpis.awaiting_admin || 0} icon={User} sub="Policy sign-off" />
          <Kpi label="Approved · lifetime" value={kpis.approved || 0} icon={CheckCircle2} muted sub={`${kpis.rejected || 0} rejected`} />
        </div>

        {/* Pipeline legend */}
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">4-stage submission pipeline</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {stages.map((s, i) => (
              <div key={s.id} className="flex items-start gap-2">
                <div className={`w-8 h-8 rounded-md grid place-items-center text-white font-black text-sm shrink-0 ${i === 3 ? "bg-[#0F9B7F]" : "bg-[#0033A0]"}`}>
                  {s.id}
                </div>
                <div>
                  <div className="font-display font-bold text-slate-900 text-sm">{s.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: "all",       label: `All (${items.length})` },
            { id: "pending",   label: `Pending (${kpis.pending || 0})` },
            { id: "Approved",  label: `Approved (${kpis.approved || 0})` },
            { id: "Rejected",  label: `Rejected (${kpis.rejected || 0})` },
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`sub-filter-${f.id}`}
              onClick={() => setStatusFilter(f.id)}
              className={`text-[11px] px-3 py-1.5 rounded-md border transition-colors ${statusFilter === f.id ? "bg-[#0033A0] text-white border-[#0033A0]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Submissions table */}
        <div className="grid gap-3" data-testid="submissions-list">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500 text-sm border border-dashed border-slate-300 rounded-md">
              No submissions in this view.
            </div>
          )}
          {filtered.map((s) => (
            <Card
              key={s.id}
              data-testid={`submission-${s.id}`}
              className="p-4 border-slate-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                <div className="w-10 h-10 rounded-md bg-[#0033A0]/10 grid place-items-center text-[#0033A0] shrink-0">
                  <Package size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-slate-900">{s.name}</span>
                    <Badge variant="outline" className={`text-[9px] ${statusBadge(s.status)}`}>{s.status}</Badge>
                    <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-600">{s.category_label}</Badge>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {s.vendor} · submitted by <span className="font-mono">{s.submitted_by}</span> {s.submitted_at && `· ${relTime(s.submitted_at)}`}
                  </div>
                  <p className="text-[12px] text-slate-600 mt-1 line-clamp-2">{s.description}</p>
                  <div className="mt-2 flex items-center gap-4 flex-wrap">
                    <StageDots stage={s.stage} stages={stages} />
                    <span className="text-[10px] text-slate-500">Stage {s.stage} of {stages.length}</span>
                    <div className="flex flex-wrap gap-1">
                      {s.personas?.map((p) => (
                        <span key={p} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{p.replace("_", " ")}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {s.status !== "Approved" && s.status !== "Rejected" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      data-testid={`reject-${s.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => openDecision(s, "reject")}
                      className="border-[#DA291C]/30 text-[#DA291C] hover:bg-[#DA291C]/5"
                    >
                      <XCircle size={13} className="mr-1" />
                      Reject
                    </Button>
                    {s.stage < 3 ? (
                      <Button
                        data-testid={`advance-${s.id}`}
                        size="sm"
                        onClick={() => openDecision(s, "advance")}
                        className="bg-[#0033A0] hover:bg-[#002573] text-white"
                      >
                        <ArrowRight size={13} className="mr-1" />
                        Advance
                      </Button>
                    ) : (
                      <Button
                        data-testid={`approve-${s.id}`}
                        size="sm"
                        onClick={() => openDecision(s, "approve")}
                        className="bg-[#0F9B7F] hover:bg-[#0d8a70] text-white"
                      >
                        <CheckCircle2 size={13} className="mr-1" />
                        Approve &amp; publish
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {s.history?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Audit trail</div>
                  <div className="space-y-1.5">
                    {s.history.slice().reverse().map((h, i) => (
                      <div key={i} className="text-[11px] flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0033A0] mt-1.5 shrink-0" />
                        <div className="flex-1">
                          <span className="font-semibold text-slate-900">{h.label}</span>
                          <span className="text-slate-500"> · {h.actor} · {relTime(h.timestamp)}</span>
                          {h.note && <div className="text-slate-600 mt-0.5">{h.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>

        <div className="text-[10px] text-slate-400 text-center pb-6">
          {brand.footer} · Flexspace Approval Governance · This page is unlinked from the main navigation.
        </div>
      </div>

      {/* Decision modal */}
      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-lg capitalize">
              {decisionAction === "advance" ? "Advance to next stage" : decisionAction === "approve" ? "Approve & publish" : "Reject submission"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {openSub?.name} · {openSub?.vendor}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Note {decisionAction === "reject" ? "(required)" : "(optional)"}</label>
            <Textarea
              data-testid="decision-note"
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              rows={3}
              className="mt-1"
              placeholder={decisionAction === "reject" ? "e.g. Missing SBOM, please resubmit" : "Optional context for the audit trail"}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionOpen(false)}>Cancel</Button>
            <Button
              data-testid="confirm-decision"
              onClick={submitDecision}
              disabled={saving || (decisionAction === "reject" && !decisionNote)}
              className={decisionAction === "reject" ? "bg-[#DA291C] hover:bg-[#b02318] text-white" : decisionAction === "approve" ? "bg-[#0F9B7F] hover:bg-[#0d8a70] text-white" : "bg-[#0033A0] hover:bg-[#002573] text-white"}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
